import { Link } from "react-router";
import { twJoin } from "tailwind-merge";

import { RichTextView } from "@/components/RichTextView/RichTextView";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { cardSurfaceVariants } from "@/design-system/Card";
import { Spread } from "@/design-system/Spread";
import { integrationIsNew } from "@/modules/portfolio/helpers/integration";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface PreprIntegration {
  _id: string;
  _slug: string | null;
  name: string | null;
  content: string | null;
  preview_content: string | null;
  created: string | null;
  profile_image: { height: number; url: string; width: number } | null;
  header_image: { height: number; url: string; width: number } | null;
}

interface AvailableIntegrationCardProps {
  integration: PreprIntegration;
  onClick: () => void;
}

export function AvailableIntegrationCard({ integration, onClick }: AvailableIntegrationCardProps): React.ReactNode {
  const { t } = useTranslation();

  const href = integration._slug ? routes.portfolio.integrationDetails({ id: integration._slug }) : "";
  const Component = href ? Link : "div";

  return (
    <Component
      className={twJoin(
        "relative flex flex-col gap-0 text-text-primary",
        cardSurfaceVariants({ interactive: Boolean(href) }),
      )}
      to={href}
      onClick={onClick}
    >
      <article className="flex flex-1 flex-col gap-space-sm p-space-md">
        <Spread>
          {integration.profile_image?.url ? (
            <img
              className="size-illustration-compact shrink-0 rounded-full border border-border-default bg-background-default object-cover p-space-xs"
              src={integration.profile_image.url}
              alt={integration.name ?? ""}
            />
          ) : null}
          {integrationIsNew(integration.created) ? (
            <Badge variant="success">{t("page.available-integrations.integration.new")}</Badge>
          ) : null}
        </Spread>
        <section className="mb-space-md flex flex-col gap-space-sm">
          <h3 className="flex min-h-control-sm w-full max-w-measure-compact items-center text-heading-3 wrap-break-word">
            {integration.name ?? ""}
          </h3>
          {integration.preview_content ? (
            <p className="whitespace-pre-line">{integration.preview_content}</p>
          ) : (
            <RichTextView preview>{integration.content ?? ""}</RichTextView>
          )}
        </section>
        {href && (
          <footer className="mt-auto flex w-full justify-end pt-space-lg">
            <Button size="sm" variant="soft">
              {t("page.available-integrations.integration.learn-more")}
            </Button>
          </footer>
        )}
      </article>
    </Component>
  );
}
